import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Input } from '../ui/input'
import { Upload, BookOpen, Loader2, Cpu } from 'lucide-react'
import { CEFR_LEVELS, LANGUAGES } from '../../types'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import { BOOK_FILE_FORMATS, isValidBookFile } from '../../lib/constants'
import { notifyTranslationStarted } from '../../lib/events/translation-events'

const AI_PROVIDERS = [
  { value: 'gemini', label: 'Google Gemini', description: 'Fast and efficient for most translations' },
  { value: 'openai', label: 'OpenAI GPT', description: 'High-quality translations with nuanced understanding' },
  { value: 'claude', label: 'Anthropic Claude', description: 'Excellent for complex simplification tasks' }
]

const formSchema = z.object({
  title: z.string().min(1, 'Book title is required'),
  targetLanguage: z.string().min(1, 'Target language is required'),
  complexityLevel: z.string().min(1, 'Complexity level is required'),
  aiProvider: z.string().min(1, 'AI provider is required'),
  bookFile: z.instanceof(File).refine((file) => {
    return file && isValidBookFile(file)
  }, `Please upload a valid book file (${BOOK_FILE_FORMATS.displayString})`)
})

type FormData = z.infer<typeof formSchema>

export default function SettingsPanel() {
  const { user } = useAuth()
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      targetLanguage: '',
      complexityLevel: '',
      aiProvider: 'gemini', // Default to Gemini
    }
  })

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setUploadedFile(file)
      form.setValue('bookFile', file)
      // Auto-fill title from filename
      const title = file.name.replace(/\.(epub|fb2|zip)$/i, '')
      form.setValue('title', title)
    }
  }

  const onSubmit = async (data: FormData) => {
    if (!user || !uploadedFile) return

    setIsProcessing(true)
    
    try {
      // Step 1: Create the user book
      const result = await apiClient.createUserBook({
        title_original: data.title,
        title_international: data.title,
        author_original: '', // Will be extracted or can be filled later
        author_international: '',
        original_file: uploadedFile,
      })

      // Step 2: Queue the translation job
      await apiClient.queueUserBookAutoTranslation(
        result.book_id,
        data.targetLanguage,
        data.complexityLevel,
        data.aiProvider
      )
      
      // Notify progress widget to start polling
      notifyTranslationStarted()
      
      // Reset form
      form.reset()
      setUploadedFile(null)
      
      // Show success and navigate to book detail page
      alert('Book uploaded successfully! Translation has been queued.')
      window.location.href = `/user/books/${result.book_id}`
    } catch (error) {
      console.error('Error processing book:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      alert(`Failed to process book: ${errorMessage}. Please try again.`)
    } finally {
      setIsProcessing(false)
    }
  }


  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          Book Processing Settings
        </CardTitle>
        <CardDescription>
          Upload a book and configure translation settings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* File Upload */}
            <FormField
              control={form.control}
              name="bookFile"
              render={() => (
                <FormItem>
                  <FormLabel>Book File</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-4">
                      <Input
                        type="file"
                        accept={BOOK_FILE_FORMATS.acceptString}
                        onChange={handleFileUpload}
                        className="flex-1"
                      />
                      {uploadedFile && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Upload className="h-4 w-4" />
                          {uploadedFile.name}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Book Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Book Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter book title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* AI Provider Selection */}
            <FormField
              control={form.control}
              name="aiProvider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AI Provider</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select AI provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AI_PROVIDERS.map((provider) => (
                        <SelectItem key={provider.value} value={provider.value}>
                          <div className="flex items-center gap-2">
                            <Cpu className="h-4 w-4" />
                            <div>
                              <div className="font-medium">{provider.label}</div>
                              <div className="text-xs text-muted-foreground">{provider.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Target Language */}
            <FormField
              control={form.control}
              name="targetLanguage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Language</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select target language" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LANGUAGES.map((language) => (
                        <SelectItem key={language} value={language}>
                          {language}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Complexity Level */}
            <FormField
              control={form.control}
              name="complexityLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reading Complexity (CEFR)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select complexity level" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CEFR_LEVELS.map((level) => (
                        <SelectItem key={level} value={level}>
                          {level} - {getCEFRDescription(level)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing Book...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload & Process Book
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

function getCEFRDescription(level: string): string {
  const descriptions: Record<string, string> = {
    'A1': 'Beginner',
    'A2': 'Elementary',
    'B1': 'Intermediate',
    'B2': 'Upper Intermediate',
    'C1': 'Advanced',
    'C2': 'Mastery'
  }
  return descriptions[level] || level
}